import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import type { ChatBroadcastMessage, ChatItem } from "@yay/shared";
import { DEFAULT_ROOM } from "@yay/shared";
import { isShowcaseRoom, useShowcasePlayground } from "./lib/showcase";
import { isBlocked, toggleBlocked } from "./lib/blockPref";
import { AvatarTheater } from "./components/AvatarTheater";
import { CapacityScreen } from "./components/CapacityScreen";
import { ChatPanel } from "./components/ChatPanel";
import { FlashcardStage } from "./components/FlashcardStage";
import { FloatingActivity } from "./components/FloatingActivity";
import { JoinOverlay } from "./components/JoinOverlay";
import { KickedScreen } from "./components/KickedScreen";
import { NerdChatPill } from "./components/NerdChatPill";
import { TopBar } from "./components/TopBar";
import { VideoPlayer } from "./components/VideoPlayer";
import { config, capacityCheckUrl, getRoomName, initSessionSync, roomMetaUrl } from "./config";
import { useRoom } from "./hooks/useRoom";

export function App() {
  useEffect(() => {
    initSessionSync();
  }, []);

  const room = useRoom();
  const [joined, setJoined] = useState(false);
  const [kickDismissed, setKickDismissed] = useState(false);
  const [chatOpen, setChatOpen] = useState(true);
  const [theaterOpen, setTheaterOpen] = useState(true);
  const [videoHidden, setVideoHidden] = useState(false);
  const [passwordRequired, setPasswordRequired] = useState(() =>
    isShowcaseRoom(getRoomName()),
  );

  // Kicked/banned and capacity are terminal: drop back to the join overlay /
  // capacity screen. Re-joining is always allowed (kicked ≠ banned) —
  // join() clears the terminal states; capacity clears when the gate reopens.
  useEffect(() => {
    if (room.kicked || room.capacity) {
      setJoined(false);
      setKickDismissed(false);
    }
  }, [room.kicked, room.capacity]);

  const nerd = room.mode === "nerd";
  const fullNerd = nerd && videoHidden;
  const canNerd = room.roomName !== DEFAULT_ROOM;
  const showcase = isShowcaseRoom(room.roomName);
  const playground = useShowcasePlayground(
    showcase,
    room.me ? { id: room.me.id, username: room.me.username } : null,
  );
  const fixtures = playground.layer;
  const showcaseUsers = useMemo(() => {
    if (!fixtures) return room.users;
    const seen = new Set(room.users.map((u) => u.id));
    if (room.me) seen.add(room.me.id);
    return [
      ...room.users,
      ...fixtures.users.filter((u) => !seen.has(u.id)),
    ];
  }, [fixtures, room.users, room.me]);

  useEffect(() => {
    if (!canNerd && room.mode === "nerd") {
      room.setMode("watch");
    }
  }, [canNerd, room.mode, room.setMode]);

  const followingLeader = room.followingUserId
    ? room.users.find((u) => u.id === room.followingUserId) ??
      (room.me?.id === room.followingUserId ? room.me : null)
    : null;

  const savedUsername = useMemo(
    () => localStorage.getItem(config.usernameStorageKey) ?? "",
    [],
  );

  // Returning from a sign-in round-trip: jump straight back into the room
  // instead of re-showing the join overlay (the OAuth redirect reloads the
  // page). Only auto-joins when a username was already saved — first-time
  // visitors still get the join screen.
  // useLayoutEffect (not useEffect) so the join overlay never paints for a
  // frame before the check runs — no flicker on the way back.
  useLayoutEffect(() => {
    if (!sessionStorage.getItem("yay:signin-return")) return;
    sessionStorage.removeItem("yay:signin-return");
    void (async () => {
      const name = getRoomName();
      let needsPass = false;
      if (name !== DEFAULT_ROOM) {
        try {
          const res = await fetch(roomMetaUrl(name));
          const body = (await res.json()) as { passwordRequired?: boolean };
          needsPass = body.passwordRequired === true;
          setPasswordRequired(needsPass);
        } catch {
          /* overlay will retry the preflight */
        }
      }
      if (needsPass) return;
      if (savedUsername.trim()) {
        setJoined(true);
        void room.join(savedUsername);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const name = getRoomName();
    if (name === DEFAULT_ROOM) {
      setPasswordRequired(false);
      return;
    }
    if (isShowcaseRoom(name)) {
      setPasswordRequired(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(roomMetaUrl(name));
        if (!res.ok) return;
        const body = (await res.json()) as { passwordRequired?: boolean };
        if (!cancelled) setPasswordRequired(body.passwordRequired === true);
      } catch {
        /* keep the username-only overlay; join will still gate */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (room.joinBlocked) setJoined(false);
  }, [room.joinBlocked]);

  const confirmedMessages = useMemo(
    () =>
      room.messages.filter((m): m is ChatItem => m.type !== "pending"),
    [room.messages],
  );

  const bubbleMessages = useMemo(() => {
    const chats = confirmedMessages.filter(
      (m): m is ChatBroadcastMessage => m.type === "chat",
    );
    return chats.slice(-12);
  }, [confirmedMessages]);

  function handleJoin(username: string, password?: string) {
    setKickDismissed(false);
    setJoined(true);
    void room.join(username, password);
  }

  /**
   * Capacity screen "Try again": re-check the cost gate; rejoin when the
   * server is accepting again (saved username → straight back in; fresh
   * visitor → join overlay). Stays on the screen while still limited.
   */
  function handleCapacityRetry() {
    void (async () => {
      try {
        const res = await fetch(capacityCheckUrl());
        if (!res.ok) return;
        const body = (await res.json()) as { state?: string };
        if (body.state === undefined || body.state !== "ok") return;
        if (savedUsername.trim()) {
          setKickDismissed(false);
          setJoined(true);
          void room.join(savedUsername);
        } else {
          room.clearCapacity();
        }
      } catch {
        /* network hiccup — stay on the screen, the user can retry */
      }
    })();
  }

  return (
    <div id="app">
      {!joined && (
        room.capacity ? (
          <CapacityScreen onRetry={handleCapacityRetry} />
        ) : room.kicked && !kickDismissed ? (
          <KickedScreen reason={room.kicked} onRejoin={() => setKickDismissed(true)} />
        ) : (
          <JoinOverlay
            initialUsername={savedUsername}
            passwordRequired={passwordRequired}
            passwordOptional={isShowcaseRoom(getRoomName())}
            passwordError={room.joinBlocked}
            onJoin={handleJoin}
          />
        )
      )}

      <div className={`main-layout${joined ? "" : " hidden"}`}>
        <TopBar
          viewerCount={showcaseUsers.length}
          status={room.status}
          chatOpen={chatOpen}
          onToggleChat={() => setChatOpen(!chatOpen)}
          mode={room.mode}
          onToggleNerd={() => {
            if (nerd) {
              // Go straight to watch — don't reset videoHidden here; that briefly
              // renders semi-nerd (nerd + visible video) before mode flips.
              room.setMode("watch");
            } else {
              setVideoHidden(true);
              setChatOpen(false);
              room.setMode("nerd");
            }
          }}
          videoHidden={videoHidden}
          onToggleVideo={() => setVideoHidden((v) => !v)}
          media={room.media}
          canSetMedia={room.roomName !== DEFAULT_ROOM}
          onSetMedia={room.setMedia}
          mediaDisabled={
            room.status !== "connected" || !room.sessionReady
          }
          canNerd={canNerd}
        />
        <div
          className={`content${nerd ? " nerd-mode" : ""}${fullNerd ? " video-hidden" : ""}`}
        >
          <div className="stage-col">
            <div
              className={`flash-slot${nerd ? "" : " stowed"}`}
              aria-hidden={!nerd}
            >
              <FlashcardStage
                cards={room.cards}
                cardIndex={room.cardIndex}
                flipped={room.flipped}
                fullBleed={fullNerd}
                followingName={
                  followingLeader
                    ? followingLeader.username.split(" ")[0]
                    : null
                }
                onUnfollow={() => room.flashFollow(null)}
                disabled={
                  !nerd ||
                  room.status !== "connected" ||
                  !room.sessionReady
                }
                onFlip={() => room.flashFlip()}
                onNav={(delta) => room.flashNav(delta)}
                onAdd={(term, definition) => room.flashAdd(term, definition)}
                onImport={(cards) => room.flashImport(cards)}
                onUpdate={(id, term, definition) =>
                  room.flashUpdate(id, term, definition)
                }
                onDelete={(id) => room.flashDelete(id)}
                editLocked={room.flashLock}
                onToggleLock={(locked) => room.flashSetLock(locked)}
                unfocusable={nerd && !chatOpen}
              />
            </div>
            <div className="left-col">
              <div className="left-col-scroll">
                <div
                  className={`player-wrap${fullNerd ? " stowed" : ""}`}
                  aria-hidden={fullNerd}
                  inert={fullNerd ? true : undefined}
                >
                  <VideoPlayer
                    media={room.media}
                    stowed={fullNerd}
                    clockOffsetMs={room.clockOffsetMs}
                    onSyncMedia={room.syncMedia}
                  />
                </div>
                <div className={`theater-spacer${theaterOpen ? "" : " push"}`} />
                <AvatarTheater
                  users={showcaseUsers}
                  me={room.me}
                  lastError={room.lastError}
                  profileSyncing={room.profileSyncing}
                  onRename={room.rename}
                  onReroll={room.reroll}
                  onClearError={room.clearError}
                  collapsed={!theaterOpen}
                  onToggleTheater={() => setTheaterOpen(!theaterOpen)}
                  nerdMode={nerd}
                  scatterBubbles={nerd && !theaterOpen}
                  scatterFullscreen={fullNerd && !theaterOpen}
                  bubbleMessages={bubbleMessages}
                  followingUserId={room.followingUserId}
                  onFollow={(userId) => {
                    if (room.followingUserId === userId) {
                      room.flashFollow(null);
                    } else {
                      room.flashFollow(userId);
                    }
                  }}
                  isOwner={room.isOwner}
                  ownerPro={room.ownerPro}
                  inPrivateRoom={room.roomName !== DEFAULT_ROOM}
                  mutedIds={room.mutedIds}
                  onCommand={room.sendCommand}
                  onWhisper={room.sendWhisper}
                  onBlock={toggleBlocked}
                  isBlocked={isBlocked}
                />
              </div>
              {joined && !chatOpen && (
                <NerdChatPill
                  fullBleed={fullNerd}
                  shiftEnd={nerd && !theaterOpen && !fullNerd}
                  disabled={
                    room.status !== "connected" ||
                    room.chatLocked ||
                    room.muted
                  }
                  onSend={room.sendChat}
                />
              )}
            </div>
          </div>
          {joined && (
            <div className={`collapsible-col${chatOpen ? " open" : ""}`}>
              <div className="collapsible-inner">
                <ChatPanel
                  messages={
                    fixtures
                      ? [...fixtures.messages, ...room.messages]
                      : room.messages
                  }
                  users={showcaseUsers}
                  me={room.me}
                  onSend={(text, replyToId) =>
                    room.sendChat(
                      text,
                      replyToId,
                      playground.replyHint(replyToId ?? ""),
                    )
                  }
                  onRetry={room.retryChat}
                  onReact={(messageId, emoji) => {
                    if (
                      room.me &&
                      playground.react(messageId, emoji, room.me.id)
                    ) {
                      return true;
                    }
                    return room.react(messageId, emoji);
                  }}
                  onReport={room.report}
                  reportOutcome={room.reportOutcome}
                  sessionReady={room.sessionReady}
                  connectionStatus={room.status}
                  disabled={room.status !== "connected"}
                  chatLocked={room.chatLocked}
                  rateLimitUntil={room.rateLimitUntil}
                  chatOpen={chatOpen}
                  isOwner={room.isOwner}
                  inPrivateRoom={room.roomName !== DEFAULT_ROOM}
                  muted={room.muted}
                  poll={room.poll ?? fixtures?.poll ?? null}
                  bannedUsers={room.bannedUsers}
                  onCommand={room.sendCommand}
                  onWhisper={room.sendWhisper}
                  onPollCreate={room.sendPollCreate}
                  onPollVote={(pollId, optionIndex) => {
                    if (
                      room.me &&
                      playground.pollVote(pollId, optionIndex, room.me.id)
                    ) {
                      return true;
                    }
                    return room.sendPollVote(pollId, optionIndex);
                  }}
                  onPollClose={(pollId) => {
                    if (playground.pollClose(pollId)) return true;
                    return room.sendPollClose(pollId);
                  }}
                  vote={room.vote ?? fixtures?.vote ?? null}
                  onVoteCast={(voteId, approve) => {
                    if (
                      room.me &&
                      playground.voteCast(voteId, approve, room.me.id)
                    ) {
                      return true;
                    }
                    return room.sendVoteCast(voteId, approve);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <FloatingActivity
        messages={confirmedMessages}
        chatOpen={chatOpen}
        theaterOpen={theaterOpen}
        nerdMode={nerd}
      />
    </div>
  );
}
